import React, { useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Link as GatsbyLink } from 'gatsby';
import { Heading, Link, Spinner } from '@printdeal/ui-library';
import { useGetOrderQuery } from '@printdeal/checkout-frontend';
import ComplaintForm from './ComplaintForm';
import Head from '../../../../PrivateHead';
import Breadcrumb from '../../../../Breadcrumb';
import { Money } from '../../../../Money';
import ComplaintCreationError from './ComplaintCreationError';

interface ComplaintCreationPageProp {
  orderId: string;
  lineItemId?: string;
}

const ComplaintCreationPage = ({ orderId, lineItemId }: ComplaintCreationPageProp) => {
  const intl = useIntl();
  const [hasError, setHasError] = useState<boolean>(false);
  const { data, isSuccess, isPending } = useGetOrderQuery({ orderId });
  const order = data?.message;

  const previousPagePath = lineItemId ? '../..' : '..';

  return (
    <>
      <Head
        title={intl.formatMessage({
          id: 'complaint.form.pageTitle',
          defaultMessage: 'Complaint Form',
        })}
      />

      <Breadcrumb className="pd-mb-4">
        <Breadcrumb.Item to={`../../..${lineItemId ? '/..' : ''}`}>
          <FormattedMessage id="account.index.title" defaultMessage="My account" />
        </Breadcrumb.Item>
        <Breadcrumb.Item to={`../..${lineItemId ? '/..' : ''}`}>
          <FormattedMessage id="complaints.title" defaultMessage="My Complaints" />
        </Breadcrumb.Item>
        <Breadcrumb.Item isCurrentPage>
          <FormattedMessage id="complaints.add.title" defaultMessage="File complaint" />
        </Breadcrumb.Item>
      </Breadcrumb>

      <div className="data-sentry-block">
        {isPending && (
          <Spinner
            variant="dim"
            screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
            className="pd-my-4"
          />
        )}
        {
          hasError && (
            <ComplaintCreationError />
          )
        }
        {(isSuccess) && (
          <>
            <Heading as="h1" className="pd-mb-6">
              <FormattedMessage id="complaint.form.title" defaultMessage="Submit Complaint" />
            </Heading>

            <Heading as="h2" className="pd-mb-4">
              <FormattedMessage
                id="complaint.form.orderItem"
                defaultMessage="1. Choose the order that concerns your complaint"
              />
            </Heading>

            <div className="pd-flex pd-items-center pd-justify-between pd-rounded pd-border pd-p-4">
              <Heading as="h3">
                <FormattedMessage
                  id="complaint.form.order.title"
                  defaultMessage="Order {orderNumber}"
                  values={{ orderNumber: order?.orderNumber }}
                />
              </Heading>
              <div className="pd-text-lg pd-text-dim-500">
                <FormattedDate value={order?.createdAt} year="numeric" month="long" day="2-digit" />
              </div>
              <div className="pd-text-lg pd-font-bold">
                <Money value={order?.taxedPrice?.totalNet.centAmount as number} />
              </div>
              <Link as={GatsbyLink} to={previousPagePath} iconBefore="times" screenReaderLabel="Back to Order List" />
            </div>
            <div className="pd-mt-2">
              <FormattedMessage
                id="complaint.form.order.incorrect.selected"
                defaultMessage="Is this not the order you're looking for? <Link>Search again</Link>"
                values={{
                  Link: (chunks) => <Link as={GatsbyLink} to={previousPagePath}>{chunks}</Link>,
                }}
              />
            </div>

            <ComplaintForm
              // @ts-expect-error type mismatch
              order={order}
              hasError={hasError}
              setHasError={setHasError}
            />

            <Link
              as={GatsbyLink}
              to="../"
              iconBefore="chevron-left"
              variant="secondary"
            >
              <FormattedMessage id="complaint.form.backToComplaintsOverview" defaultMessage="Back to My Complaints" />
            </Link>
          </>
        )}
      </div>
    </>
  );
};

export default ComplaintCreationPage;
